import React, { Fragment, useContext, useEffect } from 'react';
import {
  generatePath,
  useParams,
  useLocation,
  useHistory
} from 'react-router-dom';
import { useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ROUTE_SESSIONS,
  ROUTE_SESSIONS_ENTITY_SESSION,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT_TESTDATA,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA_EDIT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL_EDIT
} from 'routes/RouteList';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper
} from 'components/panel';

import messages from 'messages';
import RetryPanel from 'containers/partials/error-boundary/RetryPanel';
import { getAgeFromDobOnSession, getLocalDateFormat } from 'utils/date';
import { StoreContext } from 'index';
import { useSessionContext } from 'contexts/SessionContext';
import BreadcrumbPath from 'containers/partials/misc/BreadcrumbPath';
import SessionPersonTabs from 'containers/pages/sessions/SessionPersonTabs';
import { useTestDataContext } from 'contexts/TestDataContext';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import usePerson from 'hooks/queries/usePerson';
import slugify from 'slugify';
import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import ContextMenu from 'components/menu/ContextMenu';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';

const SessionPerson = () => {
  const intl = useIntl();
  const benchmarkRef = React.useRef(null);
  const {
    authStore: { user }
  } = useContext(StoreContext);
  const { entityId, sessionsTab, sessionId, personId, testDataId } =
    useParams();
  const { push } = useHistory();
  const ability = useAbility(AbilityContext);
  let location = useLocation();
  const person = useEntityContext(s => s.person);
  const { fetchPerson } = usePerson({ personId: personId });
  const { session, error: sessionError, options } = useSessionContext();
  const {
    testData,
    fetchTestData,
    activeDate,
    unsetAllData,
    setNewActiveDate
  } = useTestDataContext();

  const active = useRouteMatch([
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL_EDIT,
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL,
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE,
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA_EDIT,
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA,
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS,
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT_TESTDATA,
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON
  ]);

  const isSessionRootView = useRouteMatch({
    path: ROUTE_SESSIONS_ENTITY_SESSION_PERSON,
    strict: true,
    exact: true
  });
  const isReportView = useRouteMatch({
    path: [
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT_TESTDATA
    ],
    strict: true
  });
  const isTestsView = useRouteMatch({
    path: [
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA_EDIT,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA
    ],
    strict: true
  });

  useEffect(() => {
    if (!personId) return;
    fetchPerson(personId);
  }, [personId]);

  useEffect(() => {
    benchmarkRef.current = testData.resultBenchmarkId;
  }, [testData]);

  useEffect(() => {
    if (session?.id !== sessionId) return;

    if (
      (!testDataId && session && (isReportView || isTestsView)) ||
      isSessionRootView
    ) {
      let testData;
      if (session.isRecurring) {
        if (!activeDate) {
          const today = new Date();
          setNewActiveDate(today);
          testData = session.getTestDataByDate(personId, today);
        } else {
          testData = session.getTestDataByDate(personId, activeDate);
        }
      } else {
        testData = session.getLastTestData(personId);
      }
      if (testData?.id) {
        const selectedPath =
          testData.finishedPercentage > 0 && !isTestsView
            ? ROUTE_SESSIONS_ENTITY_SESSION_PERSON_REPORT_TESTDATA
            : ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS_TESTDATA;

        push(
          generatePath(selectedPath, {
            entityId,
            sessionId,
            sessionsTab,
            personId,
            testDataId: testData.id
          })
        );
      } else {
        if (isSessionRootView) {
          const path = ability.can('update', 'TestData')
            ? ROUTE_SESSIONS_ENTITY_SESSION_PERSON_TESTS
            : ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL;
          push(
            generatePath(path, {
              entityId,
              sessionId,
              sessionsTab,
              personId
            })
          );
        } else {
          unsetAllData();
        }
      }
    } else if (testDataId && session && (isReportView || isTestsView)) {
      if (
        !testData.id ||
        testData.id !== testDataId ||
        (benchmarkRef.current && options.benchmark !== benchmarkRef.current)
      ) {
        fetchTestData(session.id, testDataId);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testDataId, session, location, options.benchmark]);

  const isAdviceView = useRouteMatch(
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE
  );

  return (
    <>
      <Panel
        active={active.isExact}
        extraClassNames={isAdviceView ? 'u-1-of-1' : ''}
      >
        {person && (
          <>
            {sessionError && <RetryPanel />}
            {session && (
              <Fragment>
                <PanelHeader>
                  <BreadcrumbPath
                    crumbs={[
                      {
                        path: ROUTE_SESSIONS,
                        label: intl.formatMessage(messages.breadcrumbSessions)
                      },
                      {
                        path: generatePath(ROUTE_SESSIONS_ENTITY_SESSION, {
                          sessionId,
                          sessionsTab: 'sessions',
                          entityId
                        }),
                        label: intl.formatMessage(messages.breadcrumbSession, {
                          session: session.name
                        })
                      },
                      {
                        path: generatePath(ROUTE_SESSIONS_ENTITY_SESSION, {
                          sessionId,
                          sessionsTab: 'sessions',
                          entityId
                        }),
                        label: intl.formatMessage(
                          messages.breadcrumbSessionSporter
                        )
                      }
                    ]}
                  />
                  <PanelTitleWrapper>
                    <PanelTitle>
                      {person.firstname} {person.lastname}
                      {!user.isPerson() && (
                        <PanelTitleButtonsGroup>
                          <ContextMenuWrapper
                            trigger={
                              <PanelTitleButton
                                dataQa={slugify(
                                  `${person.firstname.toLowerCase()} ${person.lastname.toLowerCase()}`
                                )}
                              />
                            }
                          >
                            <ContextMenu />
                          </ContextMenuWrapper>
                        </PanelTitleButtonsGroup>
                      )}
                    </PanelTitle>
                    <PanelSubtitle>
                      {session.startdate && person.birthdate && (
                        <span>
                          <FormattedMessage
                            {...messages.labelSessionSporterAgeAtTestDate}
                            values={{
                              age: getAgeFromDobOnSession(
                                person.birthdate,
                                session.startdate
                              )
                            }}
                          />
                        </span>
                      )}
                      {session.startdate && (
                        <span>
                          <FormattedMessage
                            {...messages.labelSessionSporterSessionDate}
                            values={{
                              date: getLocalDateFormat(session.startdate)
                            }}
                          />
                        </span>
                      )}
                    </PanelSubtitle>
                  </PanelTitleWrapper>
                </PanelHeader>
                <PanelBody fullWidth={isAdviceView}>
                  {session?.id && !isSessionRootView && <SessionPersonTabs />}
                </PanelBody>
              </Fragment>
            )}
          </>
        )}
      </Panel>
    </>
  );
};

export default SessionPerson;
